/**
 * AI Agent API Service
 * Handles communication with the multi-agent backend (Session 4A APIs)
 */

import axios from 'axios';

// Base URL for AI API endpoints
// Deploy night fix 2026-05-03: read from REACT_APP_API_BASE env var so
// production points to the Render backend. Falls back to localhost for
// local dev when REACT_APP_API_BASE isn't set.
const AI_API_BASE_URL = (process.env.REACT_APP_API_BASE || 'http://localhost:5050') + '/api';

/**
 * Generate a unique session ID
 */
export const generateSessionId = () => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Get session ID from localStorage or create new one
 */
export const getSessionId = () => {
  let sessionId = localStorage.getItem('ai_session_id');
  if (!sessionId) {
    sessionId = generateSessionId();
    localStorage.setItem('ai_session_id', sessionId);
  }
  return sessionId;
};

/**
 * Clear current session (start over)
 */
export const clearSession = async () => {
  const sessionId = getSessionId();
  try {
    await axios.delete(`${AI_API_BASE_URL}/sessions/${sessionId}`);
    localStorage.removeItem('ai_session_id');
    return { success: true };
  } catch (error) {
    console.error('Error clearing session:', error);
    // Clear local storage anyway
    localStorage.removeItem('ai_session_id');
    return { success: false, error: error.message };
  }
};

/**
 * Send a message to the ConversationAgent
 *
 * @param {string} message - User's message
 * @returns {Promise} - Agent's response with extracted data
 */
export const sendChatMessage = async (message) => {
  const sessionId = getSessionId();

  try {
    const response = await axios.post(`${AI_API_BASE_URL}/chat`, {
      sessionId,
      message
    });

    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Chat API error:', error);
    return {
      success: false,
      error: error.response?.data?.details || error.message
    };
  }
};

/**
 * Start recommendation generation (async)
 *
 * @param {object} userPreferences - Extracted user preferences from chat
 * @returns {Promise} - Recommendation ID for polling
 */
export const startRecommendations = async (userPreferences) => {
  const sessionId = getSessionId();

  try {
    const response = await axios.post(`${AI_API_BASE_URL}/recommendations`, {
      sessionId,
      userPreferences
    });

    return {
      success: true,
      recommendationId: response.data.recommendationId
    };
  } catch (error) {
    console.error('Start recommendations error:', error);
    return {
      success: false,
      error: error.response?.data?.details || error.message
    };
  }
};

/**
 * Poll for recommendation results
 *
 * @param {string} recommendationId - ID from startRecommendations
 * @returns {Promise} - Current status or final results
 */
export const pollRecommendations = async (recommendationId) => {
  try {
    const response = await axios.get(`${AI_API_BASE_URL}/recommendations/${recommendationId}`);

    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Poll recommendations error:', error);
    return {
      success: false,
      error: error.response?.data?.details || error.message
    };
  }
};

/**
 * Helper: Poll until recommendations are ready
 *
 * @param {string} recommendationId - ID to poll
 * @param {function} onProgress - Callback for progress updates (optional)
 * @param {number} maxAttempts - Maximum polling attempts (default: 45 = 90 seconds)
 * @returns {Promise} - Final recommendations or error
 */
export const waitForRecommendations = async (
  recommendationId,
  onProgress = null,
  maxAttempts = 45  // 45 attempts * 2 seconds = 90 seconds
) => {
  let attempts = 0;

  while (attempts < maxAttempts) {
    attempts++;

    // Wait 2 seconds between polls
    await new Promise(resolve => setTimeout(resolve, 2000));

    const result = await pollRecommendations(recommendationId);

    if (!result.success) {
      return result;
    }

    // Call progress callback if provided
    if (onProgress) {
      onProgress({
        attempt: attempts,
        maxAttempts,
        status: result.data.status
      });
    }

    // Check if complete
    if (result.data.status === 'completed') {
      return {
        success: true,
        data: result.data
      };
    }

    // Check if error
    if (result.data.status === 'error') {
      return {
        success: false,
        error: result.data.error
      };
    }

    // Still processing, continue polling
  }

  // Timeout
  return {
    success: false,
    error: 'Recommendation generation timed out after 90 seconds. The system might be busy - please try again.'
  };
};

/**
 * Complete flow: Send message and get recommendations if ready
 * This is a convenience function that handles the full flow
 *
 * @param {string} message - User's message
 * @param {function} onProgress - Progress callback
 * @returns {Promise} - Complete result
 */
export const chatAndRecommend = async (message, onProgress = null) => {
  // Step 1: Send chat message
  const chatResult = await sendChatMessage(message);

  if (!chatResult.success) {
    return chatResult;
  }

  // Step 2: Check if ready to search
  if (chatResult.data.isReadyToSearch) {
    // Step 3: Start recommendations
    const startResult = await startRecommendations(chatResult.data.extractedData);

    if (!startResult.success) {
      return startResult;
    }

    // Step 4: Wait for results
    const recommendations = await waitForRecommendations(
      startResult.recommendationId,
      onProgress
    );

    return {
      success: recommendations.success,
      chatResponse: chatResult.data.response,
      recommendations: recommendations.data?.recommendations || null,
      error: recommendations.error
    };
  }

  // Not ready yet, return chat response
  return {
    success: true,
    chatResponse: chatResult.data.response,
    isReadyToSearch: false,
    extractedData: chatResult.data.extractedData,
    completeness: chatResult.data.completeness
  };
};

export default {
  generateSessionId,
  getSessionId,
  clearSession,
  sendChatMessage,
  startRecommendations,
  pollRecommendations,
  waitForRecommendations,
  chatAndRecommend
};
